import React from "react";
import { FormHelperText as RawErrorText, Grid, TextField, Typography } from "@material-ui/core";
import styled from "styled-components";
import { RawLinkButton } from "../../LinkButton";

import { GetMarkdown, getSelectedOptions, getTotalPrice, resetOptions } from "../../../functions/formFunctions";
import { IOption } from "../../../models/IOption";
import { ExtraSandboxAccess, FullSandboxPricing, DatabaseTypes, DurationOptions } from "./SandboxOptions";
import ISupportTicketCategory from "../../../models/ISupportTicketCategory";
import CheckboxOptionsStyled from "./CheckboxOptionsStyled";
import ReadOnlyOption from "./ReadOnlyOption";
import RadioOptions from "../../forms/RadioOptions";
import Theme from "../../../Theme";
import FormHelperText from "../../forms/FormHelperText";

const FullWidthTextField = styled(TextField)`
  min-width: 475px;
  max-width: 600px;
`;

const FormErrorText = styled(RawErrorText)`
  font-size: 14px;
  color: ${Theme.colors.error};
`;

export interface IFullSandboxRequestFormProps {
  category: ISupportTicketCategory;
  onFormSubmit: (summary: string, description: string) => Promise<boolean>;
}

const FullSandboxRequestForm = (props: IFullSandboxRequestFormProps) => {
  const { onFormSubmit, category } = props;

  const [description, setDescription] = React.useState<string>("");
  const [db, setDb] = React.useState<IOption[]>(FullSandboxPricing);
  const [extras, setExtras] = React.useState<IOption[]>(ExtraSandboxAccess);
  const [detailError, setDetailError] = React.useState<string>("");
  const [dbError, setDBError] = React.useState<string>("");
  const [dbVersions, setDbVersions] = React.useState<IOption[]>([...DatabaseTypes]);
  const [isRenewal, setIsRenewal] = React.useState<IOption[]>([...DurationOptions]);

  const allOpts = [...db, ...dbVersions, ...isRenewal, ...extras];

  const reset = () => {
    setDescription("");
    setIsRenewal([...DurationOptions]);
    setExtras(resetOptions(extras));
    setDb(resetOptions(db));
    setDbVersions(resetOptions(dbVersions));
  };

  React.useEffect(reset, [category]);

  const quoteTotal = (): number => getTotalPrice([...allOpts]);
  const hasDetailError = (): boolean => (detailError && detailError !== "" ? true : false);
  const hasDbError = (): boolean => (dbError && dbError !== "" ? true : false);

  const handleDBChange = (options: IOption[]) => {
    setDb(options);
    setDBError("");
  };

  const getFormErrors = () => {
    let hasErrors = false;

    if (!description || description === "") {
      setDetailError("Describe your need for a sandbox is a required field.");
      hasErrors = true;
    }

    if (!db[0].Selected && !db[1].Selected) {
      setDBError("A database type must be selected.");
      hasErrors = true;
    }

    if (!dbVersions[0].Selected && !dbVersions[1].Selected) {
      setDBError("A database version must be selected.");
      hasErrors = true;
    }

    return hasErrors;
  };

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!getFormErrors()) {
      const md = GetMarkdown(category.Name, description, quoteTotal(), getSelectedOptions(allOpts));

      if (await onFormSubmit("Full Sandbox Request Form", md)) {
        reset();
      }
    }
  };

  return (
    <>
      <Grid item container spacing={3}>
        <Grid item>
          <FullWidthTextField
            id="description"
            label={"Describe your need for a sandbox"}
            variant="outlined"
            multiline={true}
            rows={4}
            rowsMax={100}
            required
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
              setDescription(evt.target.value);
              setDetailError("");
            }}
            value={description}
            error={hasDetailError()}
          />
          {hasDetailError() && <FormErrorText>{detailError}</FormErrorText>}
          <FormHelperText>Include details regarding how you will utilize the sandbox</FormHelperText>
        </Grid>
        <Grid item>
          <RadioOptions label="Please select duration:" options={isRenewal} onChange={setIsRenewal} />
          <RadioOptions label="Please select the desired system access:" options={db} onChange={handleDBChange} />
          {hasDbError() && <FormErrorText>{dbError}</FormErrorText>}
          <CheckboxOptionsStyled
            options={dbVersions}
            onChange={opts => {
              setDbVersions(opts);
              setDBError("");
            }}
          >
            {"Please check version(s) requested"}
          </CheckboxOptionsStyled>
          <CheckboxOptionsStyled options={extras} onChange={opts => setExtras(opts)}>
            Please select optional features:
          </CheckboxOptionsStyled>
        </Grid>
      </Grid>
      <Typography variant="h5">Total Quote ${quoteTotal()}</Typography>
      <Grid item>
        <RawLinkButton first onClick={handleSubmit}>
          <>Submit Request</>
        </RawLinkButton>
      </Grid>
    </>
  );
};

export default FullSandboxRequestForm;
